
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { CalendarEvent } from '@/hooks/calendar/types';
import { mapDbEventToCalendarEvent } from '@/hooks/calendar/event-mappers';
import SessionRequestItem from './SessionRequestItem';
import { Button } from '@/components/ui/button';
import { RefreshCcw, AlertTriangle } from 'lucide-react';

interface SessionRequestListProps {
  onSessionStatusChange: () => void;
  onApproveRequest?: (eventId: string) => Promise<boolean>;
  onRejectRequest?: (eventId: string) => Promise<boolean>;
  onNavigateToClient?: (clientId: string) => void;
}

const SessionRequestList: React.FC<SessionRequestListProps> = ({ 
  onSessionStatusChange,
  onApproveRequest,
  onRejectRequest,
  onNavigateToClient
}) => {
  const [pendingRequests, setPendingRequests] = useState<CalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { toast } = useToast();

  const fetchPendingRequests = async () => {
    const isRefresh = !isLoading;
    if (isRefresh) setIsRefreshing(true);
    else setIsLoading(true);
    
    try {
      // Crea una mappa vuota per i colori dei clienti
      const clientColorMap = new Map<string, string>();
      
      // Prima, recupera i colori dei clienti e le informazioni sui nomi
      const { data: clientsData } = await supabase
        .from("clients")
        .select("id, first_name, last_name, color_code");
        
      if (clientsData) {
        clientsData.forEach(client => {
          if (client.id && client.color_code) {
            clientColorMap.set(client.id, client.color_code);
          }
        });
      }
      
      // Ora recupera tutte le richieste in sospeso
      const now = new Date();
      const { data, error } = await supabase
        .from('calendar_events')
        .select('*, clients:client_id(first_name, last_name)')
        .eq('status', 'pending')
        .gte('start_time', now.toISOString());
        
      if (error) throw error;
      
      // Converte i dati nel formato giusto per il calendario
      const requests = (data || []).map(dbEvent => {
        // Estrai il nome completo del cliente
        const clientName = dbEvent.clients 
          ? `${dbEvent.clients.first_name || ''} ${dbEvent.clients.last_name || ''}`.trim()
          : '';
          
        const calendarEvent = mapDbEventToCalendarEvent(dbEvent as any, clientColorMap);
        // Aggiungi il nome del cliente all'evento
        return {
          ...calendarEvent,
          clientName
        };
      });
      
      setPendingRequests(requests);
    } catch (error) {
      console.error('Errore nel recupero delle richieste pendenti:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare le richieste di sessione",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPendingRequests();
    
    // Ascolta gli aggiornamenti in tempo reale per le nuove richieste
    const channel = supabase
      .channel('calendar_events_pending')
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'calendar_events',
          filter: 'status=eq.pending'
        }, 
        () => {
          fetchPendingRequests();
        }
      )
      .on('postgres_changes', 
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'calendar_events',
          filter: 'status=eq.pending'
        }, 
        () => {
          fetchPendingRequests();
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const handleApproveSession = async (eventId: string) => {
    try {
      // Se è stata fornita una funzione di approvazione personalizzata, utilizzala
      if (onApproveRequest) {
        const success = await onApproveRequest(eventId);
        if (success) {
          fetchPendingRequests();
          onSessionStatusChange();
          
          toast({
            title: "Successo",
            description: "Richiesta di sessione approvata con successo",
          });
        }
        return;
      }
      
      // Altrimenti utilizza il metodo predefinito
      const { error } = await supabase
        .from('calendar_events')
        .update({ status: 'approved' })
        .eq('id', eventId);
        
      if (error) throw error;
      
      // Aggiorna la lista e la vista del calendario
      fetchPendingRequests();
      onSessionStatusChange();
      
      toast({
        title: "Successo",
        description: "Richiesta di sessione approvata con successo",
      });
    } catch (error) {
      console.error('Errore nell\'approvazione della sessione:', error);
      toast({
        title: "Errore",
        description: "Impossibile approvare la richiesta di sessione",
        variant: "destructive"
      });
    }
  };

  const handleRejectSession = async (eventId: string) => {
    try {
      // Se è stata fornita una funzione di rifiuto personalizzata, utilizzala
      if (onRejectRequest) {
        const success = await onRejectRequest(eventId);
        if (success) {
          fetchPendingRequests();
          onSessionStatusChange();
          
          toast({
            title: "Sessione rifiutata",
            description: "La richiesta di sessione è stata rifiutata",
          });
        }
        return;
      }
      
      // Altrimenti utilizza il metodo predefinito
      const { error } = await supabase
        .from('calendar_events')
        .update({ status: 'rejected' })
        .eq('id', eventId);
        
      if (error) throw error;
      
      // Aggiorna la lista e la vista del calendario
      fetchPendingRequests();
      onSessionStatusChange();
      
      toast({
        title: "Sessione rifiutata",
        description: "La richiesta di sessione è stata rifiutata",
      });
    } catch (error) {
      console.error('Errore nel rifiuto della sessione:', error);
      toast({
        title: "Errore",
        description: "Impossibile rifiutare la richiesta di sessione",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-medium">Richieste in attesa ({pendingRequests.length})</h2>
        <Button 
          variant="outline" 
          size="sm"
          onClick={fetchPendingRequests}
          disabled={isRefreshing}
        >
          <RefreshCcw className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>
      
      {pendingRequests.length > 0 ? (
        <div className="space-y-3">
          {pendingRequests.map(request => (
            <SessionRequestItem 
              key={request.id}
              event={request}
              onApprove={handleApproveSession}
              onReject={handleRejectSession}
            />
          ))}
        </div>
      ) : (
        <div className="text-center p-8 border rounded-md bg-gray-50">
          <AlertTriangle className="mx-auto h-8 w-8 text-yellow-500 mb-2" />
          <p className="text-gray-500">Nessuna richiesta di sessione in attesa</p>
        </div>
      )}
    </div>
  );
};

export default SessionRequestList;
